import React, { useEffect, useMemo, useState } from 'react';
import { DateShortInfo, EventShortInfo } from '../../ReportsClasses';
import { Button, Groupper, Header, Icon, Placeholder, Table } from 'react-frontier';
import { useTranslation } from 'react-i18next';
import { addCommas, bindClose } from '@arema/components/Util';
import classNames from 'classnames';
import moment from 'moment';
import API from '../../ReportsAPI';
import SectionSeatmapModal from '../SectionSeatmapModal';

import '../../style/date_sales.scss';

interface DateStatsSalesProps{
	event_id: number,
	event: EventShortInfo,
	date: DateShortInfo,
}

interface DateSalesType{
	tickets: number,
	total: number,
}

interface DateSalesSection{
	section_id: number,
	section_name: string,
	ticket_count: number,
	numbered: boolean,
	infinite: boolean,
	prices: {
		price_id: number,
		price_name: string,
		cost: number,

		cash: DateSalesType,
		card: DateSalesType,
		other: DateSalesType,
		online: DateSalesType,
		pdv: DateSalesType,
		venue: DateSalesType,

		tickets: number,
		total: number,
	}[]
}

var DateStatsSales = (props: DateStatsSalesProps)=>{
	var { t } = useTranslation();
	var [data, setData] = useState<{ date: number, sections: DateSalesSection[] }>(null);
	var [viewingSection, setViewingSection] = useState<number>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [detailed, setDetailed] = useState<boolean>(false);

	useEffect(()=>{
		if(!props.date) return;
		API.getDateSales(props.date.date_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setData(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { type: 'ST-LCL-DTSLS-1' }))
		});
	}, [props.date]);
	
	var totals = useMemo(()=>{
		var tickets = 0, total = 0;
		var others = {
			cash: { tickets: 0, total: 0 },
			card: { tickets: 0, total: 0 },
			other: { tickets: 0, total: 0 },
			online: { tickets: 0, total: 0 },
			pdv: { tickets: 0, total: 0 },
			venue: { tickets: 0, total: 0 },
		}
		if(data){
			for(var i of data.sections){
				i.prices.forEach(a=>{
					total += a.total;
					tickets += a.tickets;
					others.cash.tickets += a.cash.tickets;
					others.cash.total += a.cash.total;
					others.card.tickets += a.card.tickets;
					others.card.total += a.card.total;
					others.other.tickets += a.other.tickets;
					others.other.total += a.other.total;
					others.online.tickets += a.online.tickets;
					others.online.total += a.online.total;
					others.pdv.tickets += a.pdv.tickets;
					others.pdv.total += a.pdv.total;
					others.venue.tickets += a.venue.tickets;
					others.venue.total += a.venue.total;
				})
			}
		}
		return { 
			total, 
			tickets,
			...others
		}
	}, [data]);

	var has_income_access = useMemo(()=>{
		if(!data) return true;
		for(var i of data.sections){
			for(var p of i.prices){
				if(p.total===null && p.cost===null) return false;
			}
		}
		return true;
	}, [data]);

	var sold_seatmap = useMemo(()=>{
		if(!viewingSection) return 0;
		var sc = data.sections.find(a=>a.section_id===viewingSection);
		return sc.prices.reduce((a,b)=>a+b.tickets, 0);
	}, [viewingSection])

	if(!props.date) return null;
	if(loadError) return <Header text={t('common.error')} subtext={loadError} iconName='face-frown-open' />

	var viewSeatmap = (section_id: number)=>{
		return ()=>{
			setViewingSection(section_id);
		}
	}
	
	var SALES_HEADERS = [
		t('date_sales.table_sales.section'),
		t('date_sales.table_sales.price'),
	];
	if(has_income_access){
		SALES_HEADERS.push(t('date_sales.table_sales.cost'));
	}
	if(detailed){
		SALES_HEADERS.push(...[
			t('date_sales.table_sales.venue'),
			t('date_sales.table_sales.pdv'),
			t('date_sales.table_sales.cash'),
			t('date_sales.table_sales.online'),
		])
	}
	if(has_income_access){
		SALES_HEADERS.push(...[
			t('date_sales.table_sales.tickets'),
			t('date_sales.table_sales.sales'),
		])
	}

	var TICKETS_HEADERS = [
		'ID',
		t('date_sales.table_sales.section'),
		t('date_sales.table_sales.ticket_count'),
		t('date_sales.table_sales.tickets_sold'),
		t('date_sales.table_sales.remaining')
	]

	const EMPTY_COLOR = '#BCBCBC';

	return <div className="ar event-date-stats-sales" style={{ maxWidth: 700, margin: 'auto' }}>
		{data ? <>
			<Groupper
				fitted
				title={t('date_sales.title_sales')}
				className={classNames('sales-table', { detailed })}
				titleRight={!!has_income_access && (
					<Button size='tiny' text={t(`date_sales.${!detailed ? 'more_info' : 'less_info'}`)} onClick={bindClose(setDetailed, !detailed)} />
				)}
			>
				<div className="contents">
					<Table
						striped celled
						headers={SALES_HEADERS}
						centeredIndexes={[4,3,2]}
					>
						{data.sections.map(s=>{
							const ROW_SPAN = Math.max(s.prices.length, 1)+1+(s.prices.length>1 ? 1 : 0);
							var total_sales = 0, total_tickets = 0;
							var other_tickets = {
								venue: 0,
								cash: 0,
								card: 0,
								online: 0,
								pdv: 0,
								other: 0,
							}
							for(var i of s.prices){
								total_sales += i.total;
								total_tickets += i.tickets;
								other_tickets.venue += i.venue.tickets;
								other_tickets.card += i.card.tickets;
								other_tickets.cash += i.cash.tickets;
								other_tickets.online += i.online.tickets;
								other_tickets.pdv += i.pdv.tickets;
								other_tickets.other += i.other.tickets;
							}

							return <React.Fragment key={`STRW-${s.section_id}`}>
								<Table.Row selectable={s.numbered} onClick={s.numbered ? viewSeatmap(s.section_id) : null}>
									<Table.Cell className='section-name' style={{ borderRight: '1px solid rgba(0,0,0,.1)' }} rowSpan={ROW_SPAN}>
										{s.section_name}
										{!!s.numbered && (
											<div style={{ color: 'gray', fontSize: 12, marginTop: -3 }}>
												<Icon name="chair" />
												{t('date_sales.numbered')}
											</div>
										)}
									</Table.Cell>
								</Table.Row>
								{s.prices.length===0 && (
									<Table.Row>
										<Table.Cell colSpan={has_income_access ? 2 : 1} style={{ color: 'gray', fontStyle: 'italic' }}>
											{t('date_sales.no_sales')}
										</Table.Cell>
										<Table.Cell centered style={{ fontWeight: 'bold' }}>0</Table.Cell>
										{has_income_access && (
											<Table.Cell centered style={{ fontWeight: 'bold' }}>
												<span style={{ color: EMPTY_COLOR }}>$</span>0.00
											</Table.Cell>
										)}
									</Table.Row>
								)}
								{s.prices.map(a=>(
									<Table.Row key={`STRWP-${s.section_id}-${a.price_id}`}>
										<Table.Cell className='price-name'>{a.price_name}</Table.Cell>
										{has_income_access && (
											<Table.Cell collapsing centered>
												{a.cost!==null ? <>
													<span style={{ color: EMPTY_COLOR }}>$</span>
													{addCommas(a.cost)}
												</> : (
													<Icon name='minus' color='#CCC' style={{ margin: 0 }} />
												)}
											</Table.Cell>
										)}
										{detailed && <>
											<Table.Cell collapsing centered style={{ color: a.venue.tickets<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(a.venue.tickets, false)}</Table.Cell>
											<Table.Cell collapsing centered style={{ color: a.pdv.tickets<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(a.pdv.tickets, false)}</Table.Cell>
											<Table.Cell collapsing centered style={{ color: a.cash.tickets<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(a.cash.tickets, false)}</Table.Cell>
											<Table.Cell collapsing centered style={{ color: a.online.tickets<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(a.online.tickets, false)}</Table.Cell>
										</>}
										<Table.Cell collapsing centered style={{ fontWeight: s.prices.length==1 ? 'bold' : 'normal' }}>{addCommas(a.tickets, false)}</Table.Cell>
										{has_income_access && (
											<Table.Cell collapsing centered style={{ fontWeight: s.prices.length==1 ? 'bold' : 'normal' }}>
												{a.cost!==null ? <>
													<span style={{ color: EMPTY_COLOR }}>$</span>
													{addCommas(a.total)}
												</> : (
													<Icon name='minus' color='#CCC' style={{ margin: 0 }} />
												)}
											</Table.Cell>
										)}
									</Table.Row>
								))}
								{s.prices.length>1 && (
									<Table.Row style={{ backgroundColor: '#F3F3F3' }}>
										<Table.Cell colSpan={has_income_access ? 2 : 1} style={{ color: 'gray', fontWeight: 'bold', textAlign: 'right' }}>
											{t('common.total')}
										</Table.Cell>
										{detailed && <>
											<Table.Cell collapsing centered style={{ fontWeight: 'bold', color: other_tickets.venue<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(other_tickets.venue, false)}</Table.Cell>
											<Table.Cell collapsing centered style={{ fontWeight: 'bold', color: other_tickets.pdv<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(other_tickets.pdv, false)}</Table.Cell>
											<Table.Cell collapsing centered style={{ fontWeight: 'bold', color: other_tickets.cash<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(other_tickets.cash, false)}</Table.Cell>
											<Table.Cell collapsing centered style={{ fontWeight: 'bold', color: other_tickets.online<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(other_tickets.online, false)}</Table.Cell>
										</>}
										<Table.Cell centered style={{ fontWeight: 'bold' }}>{addCommas(total_tickets, false)}</Table.Cell>
										{has_income_access && (
											<Table.Cell centered style={{ fontWeight: 'bold' }}>
												<span style={{ color: '#818181' }}>$</span>
												{addCommas(total_sales)}
											</Table.Cell>
										)}
									</Table.Row>
								)}
							</React.Fragment>
						})}
						<Table.Divider />
						<Table.Row>
							<Table.Cell colSpan={has_income_access ? 3 : 2} style={{ fontWeight: 'bold', textAlign: 'right' }}>
								{t('common.total')}
							</Table.Cell>
							{detailed && <>
								<Table.Cell collapsing centered style={{ fontWeight: 'bold', color: totals.venue.tickets<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(totals.venue.tickets, false)}</Table.Cell>
								<Table.Cell collapsing centered style={{ fontWeight: 'bold', color: totals.pdv.tickets<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(totals.pdv.tickets, false)}</Table.Cell>
								<Table.Cell collapsing centered style={{ fontWeight: 'bold', color: totals.cash.tickets<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(totals.cash.tickets, false)}</Table.Cell>
								<Table.Cell collapsing centered style={{ fontWeight: 'bold', color: totals.online.tickets<=0 ? EMPTY_COLOR : 'black' }}>{addCommas(totals.online.tickets, false)}</Table.Cell>
							</>}
							<Table.Cell centered collapsing style={{ fontWeight: 'bold' }}>
								{addCommas(totals.tickets, false)}
							</Table.Cell>
							{has_income_access && (
								<Table.Cell centered collapsing style={{ fontWeight: 'bold' }}>
									<span style={{ color: '#818181' }}>$</span>
									{addCommas(totals.total)}
								</Table.Cell>
							)}
						</Table.Row>
					</Table>
				</div>
			</Groupper>
			<Groupper title={t('date_sales.title_progress')} fitted style={{ maxWidth: 500, margin: 'auto', marginTop: 15 }}>
				<div className="contents">
					<Table
						striped
						headers={TICKETS_HEADERS}
					>
						{data.sections.map(a=>{
							var total_tickets = 0;
							var total_sales = 0;
							for(var i of a.prices){
								total_tickets += i.tickets;
								total_sales += i.total;
							}
							return <Table.Row
								key={`PRGRW-${a.section_id}`}
								selectable={a.numbered}
								onClick={a.numbered ? viewSeatmap(a.section_id) : null}
								collapsingIndexes={[0,2,3,4]}
								centeredIndexes={[0,2,3,4]}
								data={[
									a.section_id,
									<>
										{a.section_name}
										{a.numbered && (
											<Icon name="chair" style={{ marginLeft: 5 }} color={'gray'} />
										)}
									</>, 
									a.infinite ? (
										<Icon name='infinity' style={{ margin: 0 }} />
									) : addCommas(a.ticket_count, false), 
									addCommas(total_tickets, false), 
									a.infinite ? (
										<Icon name='infinity' style={{ margin: 0 }} />
									) : addCommas(a.ticket_count-total_tickets, false),
								]}
							/>
						})}
					</Table>
				</div>
			</Groupper>
		</> : <>
			<Table
				striped celled
				title={t('date_sales.title_sales')}
				headers={SALES_HEADERS}
				centeredIndexes={[4,3,2]}
			>
				{new Array(3).fill(0).map((a, i)=>(
					<Table.Row 
						key={`PLH-${i}`}
						collapsingIndexes={[0, 2,3,4]}
						data={[
							<Placeholder width={100} />,
							<Placeholder width={70} />,
							<Placeholder width={50} random={false} />,
							<Placeholder width={50} random={false} />,
							<Placeholder width={50} random={false} />,
						]}
					/>
				))}
			</Table>
			<Table
				striped
				title={t('date_sales.title_progress')}
				style={{ maxWidth: 500, margin: 'auto', marginTop: 15 }}
				headers={TICKETS_HEADERS}
				collapsingIndexes={[1,2,3]}
				centeredIndexes={[1,2,3]}
			>
				{new Array(3).fill(0).map((a, i)=>(
					<Table.Row 
						key={`PLH-${i}`}
						collapsingIndexes={[0,2,3,4]}
						centeredIndexes={[0,2,3,4]}
						data={[
							<Placeholder width={20} />,
							<Placeholder width={100} />,
							<Placeholder width={50} random={false} />,
							<Placeholder width={50} random={false} />,
							<Placeholder width={50} random={false} />,
						]}
					/>
				))}
			</Table>
		</>}
		{!!data && (
			<div className="timestamp">{t('dashboard.timestamp')}: {moment.unix(data.date).format('DD/MM/YYYY HH:mm:ss')}</div>
		)}
		<SectionSeatmapModal
			open={!!viewingSection}
			dateId={props.date.date_id}
			sectionId={viewingSection}
			sold={sold_seatmap}
			onClose={bindClose(setViewingSection, null)}
		/>
	</div>
}

export default DateStatsSales;